import type { FlatworkEvent, JobWithContext, StageStatus } from './database.types';

/**
 * One definition of "done" for the board.
 *
 * This file exists because there were two, and they disagreed on 345 jobs.
 *
 * JobRow's status badge asked "are the PPC-controlled stages complete" and said
 * Complete. The "Slab Done · FW Pending" pill and BoardPage's archive check
 * asked "are ALL eight stages complete" and said no. So a lot with Box, Prep
 * and Pour finished but a builder inspection still open rendered a green
 * Complete badge, showed no pill, and sat on the active board with nothing
 * explaining why. That is Andrea's "the Status column is still reading as
 * complete instead of Slab done, FW pending for majority of jobs", and 345 of
 * roughly 458 active jobs is indeed the majority.
 *
 * The PPC-controlled reading is the correct one and it is the one Eric signed
 * off on in June: the board tracks PPC's work, and builder inspections lag by
 * days or weeks through no fault of the crew. Everything here keys off that.
 *
 * Every consumer imports from this file. Nothing re-derives completion locally.
 */

/** Stages PPC does not run, and which therefore never gate the board. */
export const HIDDEN_STAGE_NAMES = new Set<string>([
    // Andrea tracks Plumbing Inspection only and asked for this column to come
    // off the board. The job_stages rows stay, so no history is destroyed and
    // the column can come back by deleting one line here.
    'Plumbing',
    // Terminal marker rather than field work. It was also the quiet reason
    // Bailey Run 115 would not archive: seven of eight stages complete, the
    // eighth being this one.
    'Complete',
]);

/**
 * The slab is PPC-complete: Box, Prep and Pour all finished.
 *
 * Deliberately ignores Pin, the inspections and the terminal Complete stage.
 * Those are builder-controlled or bookkeeping and holding the board on them is
 * what produced the mismatch above.
 */
export function isSlabDone(job: JobWithContext): boolean {
    const ppc = job.stages.filter((s) => s.stage.ppc_controlled);
    return ppc.length > 0 && ppc.every((s) => s.status === 'complete');
}

/**
 * Flatwork is finished.
 *
 * A lot with no flatwork rows at all is NOT done, it is unscheduled. That
 * distinction is load-bearing: 345 lots are flagged flatwork_active with no
 * rows yet, and treating absence as completion would archive every one of them.
 */
export function isFlatworkDone(flatwork: FlatworkEvent[]): boolean {
    return flatwork.length > 0 && flatwork.every((f) => f.status === 'complete');
}

/**
 * Slab poured, flatwork still outstanding. Drives the "Slab Done · FW Pending"
 * pill, which is the row's way of saying exactly what it is waiting for.
 */
export function isWaitingOnFlatwork(job: JobWithContext, flatwork: FlatworkEvent[]): boolean {
    return isSlabDone(job) && job.flatwork_active && !isFlatworkDone(flatwork);
}

/**
 * The job belongs on the Completed tab.
 *
 * force_active wins over everything. That is how a job marked complete by
 * mistake comes back to the board without touching stage history. The
 * alternative was clearing Pour's actual_completion_date, which would silently
 * break that lot's weather packet, since the prep-to-pour window is derived
 * from precisely that date.
 */
export function isJobCompleted(job: JobWithContext, flatwork: FlatworkEvent[]): boolean {
    if (job.force_active) return false;
    if (!isSlabDone(job)) return false;
    if (!job.flatwork_active) return true;
    return isFlatworkDone(flatwork);
}

/**
 * The single status the row reports in the Status column, when it is not
 * showing the flatwork pill.
 *
 * Complete once PPC's own work is done. On hold if any stage is held, because a
 * hold is the thing someone needs to see. Otherwise the status of the earliest
 * unfinished stage, which is the honest answer to "where is this job".
 */
export function overallStatus(job: JobWithContext): StageStatus {
    if (isSlabDone(job)) return 'complete';

    const inOrder = [...job.stages].sort(
        (a, b) => a.stage.sequence_order - b.stage.sequence_order
    );

    if (inOrder.some((s) => s.status === 'on_hold')) return 'on_hold';

    // Hidden stages are not work anyone is waiting on, so they never become the
    // reported status.
    const firstOpen = inOrder.find(
        (s) => s.status !== 'complete' && !HIDDEN_STAGE_NAMES.has(s.stage.name)
    );
    return firstOpen ? firstOpen.status : 'complete';
}

/** Stage columns the board renders, in sequence order. */
export function visibleStages<T extends { name: string; sequence_order: number }>(stages: T[]): T[] {
    return stages
        .filter((s) => !HIDDEN_STAGE_NAMES.has(s.name))
        .sort((a, b) => a.sequence_order - b.sequence_order);
}

/**
 * Stages the waterfall can cascade from. Matches the RPC's accepted anchors.
 *
 * Was Pin, Box and Prep only. Those three were chosen when the offsets were
 * uneven and derived from real rows, so only the stages Andrea actually
 * re-entered had sensible anchor arithmetic. The offsets are uniform now, one
 * business day per stage, which makes every stage a valid anchor.
 *
 * Andrea, 29 Jul: "If I have to go in and change the plumbing date because it
 * got pushed, any task after that should automatically waterfall."
 *
 * Pour is excluded on purpose. It is last, so cascading from it would write
 * nothing, and a checkbox that visibly does nothing reads as broken.
 */
export const CASCADE_ANCHORS = new Set<string>([
    'Pin',
    'Box',
    'Plumbing Inspection',
    'Prep',
    'Slab Inspection',
]);
